import { ReactNode, useCallback } from 'react';
import { format } from 'date-fns';
import { Typography, Grid } from '@mui/material';
import theme from 'App.theme';
import { Constant } from 'utils/consts';
import { IconType } from 'components/Icon/Icon.model';
import { Icon } from 'components/Icon';

import LinkTextAndIcon from './LinkTextAndIcon';

export type INotificationColor = 'error' | 'warning' | 'success' | 'info';

interface INotificationItem {
  icon: IconType;
  color: INotificationColor;
  text: ReactNode;
  linkText: string;
  fontWeight?: number;
  withoutBorder?: boolean;
  isLinkTextAndIconHide?: boolean;
}

const NotificationItem = ({
  icon,
  color,
  text,
  linkText,
  fontWeight = Constant.sixHundred,
  withoutBorder,
  isLinkTextAndIconHide
}: INotificationItem) => {
  const getColor = useCallback(() => {
    switch (color) {
      case 'success': {
        return theme.palette.success.main;
      }
      case 'warning': {
        return theme.palette.warning.main;
      }
      case 'info': {
        return theme.palette.info.main;
      }
      default: {
        return theme.palette.error.main;
      }
    }
  }, [color]);

  return (
    <Grid
      container
      sx={{
        paddingBottom: '25px',
        borderBottom: withoutBorder
          ? 'none'
          : `1px solid ${theme.palette.secondary.light}`
      }}
    >
      <Grid
        size={12}
        sx={{
          pb: 1
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: 'common.darkGrey'
          }}
        >
          {format(new Date(), 'dd.MM.yyyy')}
        </Typography>
      </Grid>
      <Grid
        size={12}
        container
        sx={{
          alignItems: 'center',
          justifyContent: `${
            isLinkTextAndIconHide ? 'flex-start' : 'space-between'
          }`
        }}
      >
        <Grid
          size={1}
          sx={{
            pr: '12px',
            position: 'relative',
            top: '3px'
          }}
        >
          <Icon
            icon={icon}
            fontSize="small"
            color={color}
            sx={{ verticalAlign: 'baseline' }}
          />
        </Grid>
        <Grid
          size={{ xs: 9, lg: 8 }}
          sx={{
            pr: { xs: 1, md: 2 }
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight, whiteSpace: 'break-spaces' }}
          >
            {text}
          </Typography>
        </Grid>
        {!isLinkTextAndIconHide && (
          <LinkTextAndIcon
            linkText={linkText}
            color={color}
            iconColor={getColor()}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default NotificationItem;
